import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Filter, 
  Save, 
  ChevronDown, 
  Check, 
  AlertCircle, 
  Loader2, 
  Brain,
  CheckCircle,
  History,
  Bookmark,
  Clock,
  Sparkles,
  Building2,
  TrendingUp,
  Users,
  DollarSign,
  Target,
  Zap,
  Globe,
  Calendar,
  Award,
  Briefcase
} from 'lucide-react';
import { Button } from '../../../components/ui/Button/Button';
import { cn } from '../../../utils/cn';
import SearchResultsSection from '../components/SearchResultsSection';
import SaveSearchModal from '../components/SaveSearchModal';
import { ApiService } from '../../../services/apiService';

// Types for People Search
interface SearchContact {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone?: string;
  jobTitle: string;
  company: {
    name: string;
    domain: string;
    industry: string;
    size: string;
    location: string;
    logo?: string;
  };
  location: {
    city: string;
    state: string;
    country: string;
  };
  socialProfiles: {
    linkedin?: string;
    twitter?: string;
    github?: string;
  };
  leadScore: number;
  intentScore: number;
  emailVerificationStatus: 'verified' | 'unverified' | 'invalid' | 'pending';
  lastActivity?: string;
  technologies?: string[];
  dataSourceAttribution: string[];
  freshnessScore: number;
  qualityScore: number;
  avatar?: string;
}

interface SearchFilters {
  jobTitles: string[];
  companies: string[];
  industries: string[];
  companySizes: string[];
  locations: string[];
  technologies: string[];
  emailVerified: boolean;
  leadScoreRange: [number, number];
  intentScoreRange: [number, number];
  salaryRange?: [number, number];
  experienceRange?: [number, number];
  departments: string[];
  seniority: string[];
  lastActivityDays?: number;
}

interface SavedSearch {
  id: string;
  name: string;
  query: string;
  filters: SearchFilters;
  alertsEnabled: boolean;
  createdAt: string;
  lastRun: string;
  resultCount: number;
}

interface SearchQuery {
  query: string;
  filters: SearchFilters;
  page: number;
  limit: number;
  sortBy: 'relevance' | 'leadScore' | 'lastActivity' | 'alphabetical';
  sortOrder: 'asc' | 'desc';
}

interface LLMInsights {
  reasoning: string;
  confidence: number;
  extractedFilters: Partial<SearchFilters>;
  method: 'LLM Enhanced' | 'Boolean Fallback';
}

interface SearchResults {
  contacts: SearchContact[];
  totalCount: number;
  page: number;
  hasMore: boolean;
  aggregations: {
    industries: Array<{ name: string; count: number }>;
    companies: Array<{ name: string; count: number }>;
    locations: Array<{ name: string; count: number }>;
    jobTitles: Array<{ name: string; count: number }>;
  };
  searchTime: number;
  llmInsights?: LLMInsights | null;
}

// Cycling text component
const CyclingText: React.FC = () => {
  const cyclingTexts = [
    "CEOs at AI startups",
    "VPs of Sales at SaaS companies", 
    "Software engineers with React",
    "Marketing directors at Fortune 500",
    "CTOs in Silicon Valley",
    "Product managers in fintech",
    "Sales directors with verified emails",
    "Engineers at recently funded startups"
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % cyclingTexts.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={currentIndex}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.5 }}
        className="text-primary-500"
      >
        {cyclingTexts[currentIndex]}
      </motion.span>
    </AnimatePresence>
  );
};

// Compact Filter Section Component
const CompactFiltersPanel: React.FC<{
  filters: SearchFilters;
  onFilterChange: (key: keyof SearchFilters, value: any) => void;
  aggregations?: any;
}> = ({ filters, onFilterChange, aggregations }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['jobTitles']));

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const FilterSection: React.FC<{
    title: string;
    section: string;
    children: React.ReactNode;
    count?: number;
  }> = ({ title, section, children, count }) => (
    <div className="border-b border-neutral-200 dark:border-neutral-700 last:border-b-0">
      <button
        onClick={() => toggleSection(section)}
        className="w-full flex items-center justify-between py-3 px-4 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-left"
      >
        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{title}</span>
        <div className="flex items-center space-x-2">
          {count && count > 0 && (
            <span className="bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-1 rounded-full text-xs font-medium">
              {count}
            </span>
          )}
          <ChevronDown 
            className={cn(
              "w-4 h-4 text-neutral-500 transition-transform",
              expandedSections.has(section) && "rotate-180"
            )}
          />
        </div>
      </button>
      <AnimatePresence>
        {expandedSections.has(section) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-3">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const CustomCheckbox: React.FC<{
    checked: boolean;
    onChange: (checked: boolean) => void;
    label: string;
    count?: number;
  }> = ({ checked, onChange, label, count }) => (
    <label className="flex items-center justify-between py-1 cursor-pointer group">
      <div className="flex items-center">
        <div className={cn(
          "w-4 h-4 rounded border-2 flex items-center justify-center transition-all",
          checked 
            ? "bg-primary-500 border-primary-500" 
            : "border-neutral-300 dark:border-neutral-600 group-hover:border-primary-400"
        )}>
          {checked && <Check className="w-3 h-3 text-white" />}
        </div>
        <span className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">{label}</span>
      </div>
      {count && (
        <span className="text-xs text-neutral-500">{count}</span>
      )}
    </label>
  );

  return (
    <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg">
      <div className="p-4 border-b border-neutral-200 dark:border-neutral-700">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 flex items-center">
          <Filter className="w-4 h-4 mr-2" />
          Filters
        </h3>
      </div>

      <FilterSection title="Job Titles" section="jobTitles" count={filters.jobTitles.length}>
        <div className="space-y-1">
          {['CEO', 'CTO', 'VP Sales', 'VP Marketing', 'Director', 'Manager', 'Engineer', 'Analyst'].map(title => (
            <CustomCheckbox
              key={title}
              checked={filters.jobTitles.includes(title)}
              onChange={(checked) => {
                const newTitles = checked 
                  ? [...filters.jobTitles, title]
                  : filters.jobTitles.filter(t => t !== title);
                onFilterChange('jobTitles', newTitles);
              }}
              label={title}
              count={aggregations?.jobTitles?.find((j: any) => j.name.includes(title))?.count}
            />
          ))}
        </div>
      </FilterSection>

      <FilterSection title="Industries" section="industries" count={filters.industries.length}>
        <div className="space-y-1">
          {['Technology', 'SaaS', 'Fintech', 'Healthcare', 'E-commerce', 'AI/ML', 'Cybersecurity'].map(industry => (
            <CustomCheckbox
              key={industry}
              checked={filters.industries.includes(industry)}
              onChange={(checked) => {
                const newIndustries = checked 
                  ? [...filters.industries, industry]
                  : filters.industries.filter(i => i !== industry);
                onFilterChange('industries', newIndustries);
              }}
              label={industry}
              count={aggregations?.industries?.find((i: any) => i.name.includes(industry))?.count}
            />
          ))}
        </div>
      </FilterSection>

      <FilterSection title="Company Size" section="companySizes" count={filters.companySizes.length}>
        <div className="space-y-1">
          {['1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5000+'].map(size => (
            <CustomCheckbox
              key={size}
              checked={filters.companySizes.includes(size)}
              onChange={(checked) => {
                const newSizes = checked 
                  ? [...filters.companySizes, size]
                  : filters.companySizes.filter(s => s !== size);
                onFilterChange('companySizes', newSizes);
              }}
              label={`${size} employees`}
            />
          ))}
        </div>
      </FilterSection>

      <FilterSection title="Location" section="locations" count={filters.locations.length}>
        <div className="space-y-1">
          {['San Francisco', 'New York', 'Los Angeles', 'Austin', 'Seattle', 'Boston', 'Chicago'].map(location => (
            <CustomCheckbox
              key={location}
              checked={filters.locations.includes(location)}
              onChange={(checked) => {
                const newLocations = checked 
                  ? [...filters.locations, location]
                  : filters.locations.filter(l => l !== location);
                onFilterChange('locations', newLocations);
              }}
              label={location}
              count={aggregations?.locations?.find((l: any) => l.name.includes(location))?.count}
            />
          ))}
        </div>
      </FilterSection>

      <FilterSection title="Email Status" section="email">
        <CustomCheckbox
          checked={filters.emailVerified}
          onChange={(checked) => onFilterChange('emailVerified', checked)}
          label="Verified emails only"
        />
      </FilterSection>
    </div>
  );
};

// Main People Search Component
export const PeopleSearchPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({
    jobTitles: [],
    companies: [],
    industries: [],
    companySizes: [],
    locations: [],
    technologies: [],
    emailVerified: false,
    leadScoreRange: [0, 100],
    intentScoreRange: [0, 100],
    departments: [],
    seniority: []
  });
  const [results, setResults] = useState<SearchResults | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [recentSearches, setRecentSearches] = useState<string[]>([
    'VPs of Sales at SaaS companies',
    'Software engineers with React',
    'CTOs in Silicon Valley',
    'Marketing directors at Fortune 500'
  ]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false); // Default to closed
  const [showRecentSearches, setShowRecentSearches] = useState(false);
  const [showSavedSearches, setShowSavedSearches] = useState(false);
  const [sortBy, setSortBy] = useState<'relevance' | 'leadScore' | 'lastActivity' | 'alphabetical'>('relevance');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Natural language search examples
  const searchExamples = [
    "Find VPs of Sales at SaaS companies in California with verified emails",
    "Software engineers with React experience at startups, recently active",
    "Marketing directors at Fortune 500 companies who use HubSpot",
    "CTOs at AI companies in Silicon Valley with 50-200 employees",
    "Senior product managers in fintech with 5+ years experience"
  ];

  // Check if search has been initiated
  const hasSearched = searchQuery.trim().length > 0 || results !== null;

  // Update the debouncedSearch function to use ApiService
  const debouncedSearch = useCallback(
    debounce(async (query: string, searchFilters: SearchFilters) => {
      if (!query.trim() && Object.values(searchFilters).every(v => 
        Array.isArray(v) ? v.length === 0 : v === false || v === 0 || (Array.isArray(v) && v[0] === 0 && v[1] === 100)
      )) {
        setResults(null);
        return;
      }

      setIsSearching(true);
      try {
        const searchParams = {
          query,
          filters: searchFilters,
          page: 1,
          limit: 50,
          sortBy,
          sortOrder
        };

        const response = await ApiService.searchPeople(searchParams);

        if (response.success) {
          setResults(response.data);
          
          // Add to recent searches if it's a new query
          if (query.trim() && !recentSearches.includes(query.trim())) {
            setRecentSearches(prev => [query.trim(), ...prev.slice(0, 4)]);
          }
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    [sortBy, sortOrder, recentSearches]
  );

  // Trigger search when query or filters change
  useEffect(() => {
    debouncedSearch(searchQuery, filters);
  }, [searchQuery, filters, debouncedSearch]);

  // Search suggestions state
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Update the getSuggestions function to use ApiService
  const getSuggestions = useCallback(
    debounce(async (query: string) => {
      if (query.length < 2) {
        setSuggestions([]);
        return;
      }

      try {
        const response = await ApiService.getPeopleSuggestions(query);
        if (response.success) {
          setSuggestions(response.data?.suggestions || []);
        }
      } catch (error) {
        console.error('Suggestions error:', error);
      }
    }, 150),
    []
  );

  useEffect(() => {
    getSuggestions(searchQuery);
  }, [searchQuery, getSuggestions]);

  const handleSearchQueryChange = (value: string) => {
    setSearchQuery(value);
    setShowSuggestions(value.length > 0);
    setShowRecentSearches(false);
  };

  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleContactSelection = (contactId: string, selected: boolean) => {
    setSelectedContacts(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(contactId);
      } else {
        newSet.delete(contactId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (results) {
      const allIds = results.contacts.map(c => c.id);
      setSelectedContacts(new Set(allIds));
    }
  };

  const handleClearSelection = () => {
    setSelectedContacts(new Set());
  };

  const handleBulkExport = (format: 'csv' | 'json') => {
    if (selectedContacts.size === 0) return;
    
    const selectedContactData = results?.contacts.filter(c => selectedContacts.has(c.id)) || [];
    console.log(`Exporting ${selectedContactData.length} contacts as ${format}`);
  };

  // Update the handleSaveSearch function to use ApiService
  const handleSaveSearch = async (name: string, enableAlerts: boolean = false) => {
    const savedSearch: SavedSearch = {
      id: Date.now().toString(),
      name,
      query: searchQuery,
      filters,
      alertsEnabled: enableAlerts,
      createdAt: new Date().toISOString(),
      lastRun: new Date().toISOString(),
      resultCount: results?.totalCount || 0
    };

    setSavedSearches(prev => [...prev, savedSearch]);
    setShowSaveModal(false);
    
    try {
      await ApiService.savePeopleSearch(savedSearch);
    } catch (error) {
      console.error('Error saving search:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
      {/* Main Content with Far Left Filters */}
      <div className="transition-all duration-300 flex">
        {/* Compact Filters Sidebar - Far Left */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="w-72 flex-shrink-0 bg-white dark:bg-neutral-800 border-r border-neutral-200 dark:border-neutral-700 min-h-screen"
            >
              <div className="sticky top-0 p-4">
                <CompactFiltersPanel 
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  aggregations={results?.aggregations}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Search Content */}
        <div className="flex-1 min-w-0">
          {/* Central Search Section */}
          <div className="max-w-5xl mx-auto px-8 py-12">
            {/* Hero Text with Cycling Animation */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <h1 className="text-5xl font-bold text-neutral-900 dark:text-neutral-100 mr-6">
                  Find
                </h1>
                <div className="text-5xl font-bold h-[7rem] w-[42rem] flex items-center justify-center text-center leading-tight">
                  <CyclingText />
                </div>
              </div>
              <p className="text-xl text-neutral-600 dark:text-neutral-400 flex items-center justify-center">
                <Brain className="w-6 h-6 mr-3 text-primary-500" />
                AI-powered search • Describe who you're looking for in natural language
              </p>
            </div>

            {/* Large Search Bar */}
            <div className="relative mb-8">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none">
                  <Search className="h-6 w-6 text-neutral-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchQueryChange(e.target.value)}
                  placeholder="Describe the people you're looking for..."
                  className="block w-full pl-16 pr-16 py-6 text-xl border border-neutral-300 dark:border-neutral-600 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 shadow-lg"
                  onFocus={() => {
                    setShowSuggestions(searchQuery.length > 0);
                    setShowRecentSearches(searchQuery.length === 0);
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setShowSuggestions(false);
                      setShowRecentSearches(false);
                    }, 200);
                  }}
                />
                <div className="absolute inset-y-0 right-0 pr-6 flex items-center">
                  {isSearching ? (
                    <Loader2 className="h-6 w-6 text-primary-500 animate-spin" />
                  ) : (
                    <button
                      onClick={() => setShowRecentSearches(!showRecentSearches)}
                      className="p-2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                    >
                      <Clock className="h-5 w-5" />
                    </button>
                  )}
                </div>

                {/* Recent Searches Dropdown */}
                {showRecentSearches && recentSearches.length > 0 && (
                  <div className="absolute z-20 w-full mt-2 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-xl">
                    <div className="p-3">
                      <div className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-3 flex items-center">
                        <History className="w-4 h-4 mr-2" />
                        Recent Searches
                      </div>
                      {recentSearches.map((search, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setSearchQuery(search);
                            setShowRecentSearches(false);
                          }}
                          className="w-full text-left px-3 py-3 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg text-base text-neutral-700 dark:text-neutral-300"
                        >
                          {search}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Saved Searches Dropdown */}
                {showSavedSearches && savedSearches.length > 0 && (
                  <div className="absolute z-20 w-full mt-2 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-xl">
                    <div className="p-3">
                      <div className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-3 flex items-center">
                        <Bookmark className="w-4 h-4 mr-2" />
                        Saved Searches
                      </div>
                      {savedSearches.map((search) => (
                        <button
                          key={search.id}
                          onClick={() => {
                            setSearchQuery(search.query);
                            setFilters(search.filters);
                            setShowSavedSearches(false);
                          }}
                          className="w-full text-left px-3 py-3 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg text-base"
                        >
                          <div className="font-medium text-neutral-700 dark:text-neutral-300">{search.name}</div>
                          <div className="text-sm text-neutral-500 dark:text-neutral-400">{search.resultCount} results</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search Suggestions */}
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-2 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-xl">
                    {suggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setSearchQuery(suggestion);
                          setShowSuggestions(false);
                        }}
                        className="w-full text-left px-6 py-3 hover:bg-neutral-100 dark:hover:bg-neutral-700 first:rounded-t-xl last:rounded-b-xl text-base text-neutral-700 dark:text-neutral-300"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Subtle Action Buttons */}
            <div className="flex items-center justify-center space-x-4 mb-8">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 flex items-center transition-colors"
              >
                <Filter className="w-4 h-4 mr-2" />
                {showFilters ? 'Hide' : 'Show'} Filters
              </button>
              <span className="text-neutral-300 dark:text-neutral-600">•</span>
              <button
                onClick={() => setShowSavedSearches(!showSavedSearches)}
                className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 flex items-center transition-colors"
              >
                <Bookmark className="w-4 h-4 mr-2" />
                Saved Searches
              </button>
              <span className="text-neutral-300 dark:text-neutral-600">•</span>
              <button
                onClick={() => setShowSaveModal(true)}
                disabled={!results}
                className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 flex items-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Search
              </button>
            </div>

            {/* Try These Examples - Only when not searched */}
            {!hasSearched && (
              <div className="text-center mb-8">
                <p className="text-base font-medium text-white mb-4 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 mr-2 text-blue-400" />
                  Try these examples:
                </p>
                <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
                  {searchExamples.map((example, index) => (
                    <button
                      key={index}
                      onClick={() => setSearchQuery(example)}
                      className="px-4 py-2 text-sm bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur-xl border border-blue-500/20 text-blue-300 rounded-full hover:from-blue-500/20 hover:to-purple-500/20 hover:border-blue-400/30 transition-all duration-300 hover:scale-105"
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* AI Insights Display */}
            {results?.llmInsights && (
              <div className="mb-8">
                <div className="p-6 bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur-xl rounded-2xl border border-blue-500/20 shadow-2xl">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      {results.llmInsights.confidence > 0.7 ? (
                        <CheckCircle className="w-6 h-6 text-green-400" />
                      ) : (
                        <AlertCircle className="w-6 h-6 text-yellow-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-base font-medium text-white flex items-center">
                          <Brain className="w-5 h-5 mr-2 text-blue-400" />
                          AI Insights
                        </h4>
                        <div className="flex items-center space-x-3">
                          <span className="text-sm text-neutral-300">
                            {results.llmInsights.method}
                          </span>
                          <div className={cn(
                            "px-3 py-1 rounded-full text-sm font-medium backdrop-blur-xl",
                            results.llmInsights.confidence > 0.8 
                              ? "bg-green-500/20 text-green-300 border border-green-500/30"
                              : results.llmInsights.confidence > 0.6
                              ? "bg-yellow-500/20 text-yellow-300 border border-yellow-500/30"
                              : "bg-red-500/20 text-red-300 border border-red-500/30"
                          )}>
                            {Math.round(results.llmInsights.confidence * 100)}% confidence
                          </div>
                        </div>
                      </div>
                      <p className="text-base text-neutral-200 mb-3">
                        {results.llmInsights.reasoning}
                      </p>
                      {Object.keys(results.llmInsights.extractedFilters).length > 0 && (
                        <div>
                          <p className="text-sm text-neutral-300 mb-2">Detected filters:</p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(results.llmInsights.extractedFilters).map(([key, value]) => (
                              <span
                                key={key}
                                className="px-3 py-1 bg-blue-500/20 text-blue-300 text-sm rounded-full border border-blue-500/30 backdrop-blur-xl"
                              >
                                {key}: {Array.isArray(value) ? value.join(', ') : String(value)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="opacity-0">
                {/* Hidden placeholder to maintain layout */}
                <Button variant="outline">Show Filters</Button>
              </div>
              
              {results && (
                <div className="text-base text-neutral-300">
                  {results.totalCount.toLocaleString()} people found in {results.searchTime}ms
                </div>
              )}
            </div>

            {/* Search Results */}
            <SearchResultsSection
              results={results}
              isSearching={isSearching}
              selectedContacts={selectedContacts}
              onContactSelection={handleContactSelection}
              onSelectAll={handleSelectAll}
              onClearSelection={handleClearSelection}
              onBulkExport={handleBulkExport}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={(sort, order) => {
                setSortBy(sort as 'relevance' | 'leadScore' | 'lastActivity' | 'alphabetical');
                setSortOrder(order as 'asc' | 'desc');
              }}
            />
          </div>
        </div>
      </div>

      {/* Save Search Modal */}
      <SaveSearchModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSaveSearch}
        currentQuery={searchQuery}
        resultCount={results?.totalCount || 0}
      />
    </div>
  );
};

// Utility function for debouncing
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export default PeopleSearchPage; 